import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { formatCurrency } from '../../utils/formatters';

export interface ReportPieChartDatum {
  name: string;
  value: number;
}

const SLICE_COLORS = [
  '#1976d2', // primary
  '#2e7d32', // success
  '#ed6c02', // warning
  '#9c27b0', // purple
  '#0288d1', // info
  '#d32f2f', // error
  '#5c6bc0', // indigo
  '#00897b', // teal
  '#f9a825', // amber
  '#6d4c41', // brown
];

interface ReportPieChartProps {
  data: ReportPieChartDatum[];
  height?: number;
  emptyLabel?: string;
}

export const ReportPieChart: React.FC<ReportPieChartProps> = ({ data, height = 300, emptyLabel }) => {
  const { t } = useTranslation();
  const positiveData = data.filter((d) => d.value > 0);
  const resolvedEmptyLabel = emptyLabel ?? t('reports.noData', 'No data for this period');

  if (positiveData.length === 0) {
    return (
      <Box
        sx={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {resolvedEmptyLabel}
        </Typography>
      </Box>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie
          data={positiveData}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy="50%"
          outerRadius={height / 3.2}
          label={(entry) => `${entry.name}`}
        >
          {positiveData.map((entry, index) => (
            <Cell key={entry.name} fill={SLICE_COLORS[index % SLICE_COLORS.length]} />
          ))}
        </Pie>
        <Tooltip formatter={(value: number) => formatCurrency(value)} />
        <Legend />
      </PieChart>
    </ResponsiveContainer>
  );
};
